/**
 * CityDetail — öffentliche Stadt-Detailseite (Städteseiten-Relaunch 09.2026)
 *
 * v1-Titel-Vorbild: "Helium Hotspots in <Stadt> <Land>". Zeigt Kennzahlen der
 * Stadt plus eine gedeckelte, paginierte Hotspot-Liste (Backend deckelt
 * CITY_HOTSPOT_LIMIT, siehe Api::V2::CitiesController#show).
 */
import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useCityDetail } from './useCityDetail'
import { fmt } from '../../lib/fmt'
import { setPublicPageMeta, publicUrl } from '../../lib/seo'
import './cities-public.css'

type Props = {
  id: string
}

export function CityDetail({ id }: Props) {
  const { t } = useTranslation('cities')
  const [page, setPage] = useState(1)

  // Code-Review-Fix: die Komponente bleibt beim Navigieren von einer Stadt
  // zur naechsten (z.B. ueber einen Hotspot-Link) als dieselbe Instanz
  // gemountet, `page` blieb ohne diesen Reset auf dem zuletzt gewaehlten
  // Wert stehen — eine neu geoeffnete Stadt startete dann auf einer fremden
  // Seitenzahl statt auf Seite 1. Absichtlich WAEHREND des Renderns
  // angepasst (React-Empfehlung fuer "State bei Prop-Wechsel zuruecksetzen"),
  // nicht in einem Effekt — ein setState im Effekt wuerde eine zusaetzliche,
  // sichtbare Renderrunde ausloesen statt still vor dem Commit zu greifen.
  const [prevId, setPrevId] = useState(id)
  if (id !== prevId) {
    setPrevId(id)
    setPage(1)
  }

  const { data, isLoading, isError, refetch } = useCityDetail(id, page)
  const city = data?.data

  useEffect(() => {
    if (!city) return
    const canonical = publicUrl(`/cities/${id}`)
    return setPublicPageMeta({
      title: t('detail.seoTitle', { city: city.name, country: city.country_code }),
      description: t('detail.seoDescription', { city: city.name, country: city.country_code }),
      canonical,
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'Place',
        name: city.name,
        url: canonical,
        address: {
          '@type': 'PostalAddress',
          addressLocality: city.name,
          addressCountry: city.country_code,
        },
      },
    })
  }, [city, id, t])

  if (isLoading) {
    return (
      <div className="city-detail-page" data-testid="city-detail-loading">
        <div className="skeleton" style={{ height: 130, marginBottom: 16 }} />
        <div className="skeleton" style={{ height: 320 }} />
      </div>
    )
  }

  if (isError || !city) {
    return (
      <div className="city-detail-page card card-pad state-error" data-testid="city-detail-error">
        <p>{t('detail.error')}</p>
        <button type="button" className="btn" onClick={() => refetch()}>
          {t('detail.retry')}
        </button>
      </div>
    )
  }

  const meta = data?.meta

  return (
    <div className="city-detail-page" data-testid="city-detail-page">
      <div className="section-header">
        <div>
          <h1>{t('detail.title', { city: city.name, country: city.country_code })}</h1>
          <p className="muted">{t('detail.subtitle', { count: city.hotspots_count })}</p>
        </div>
      </div>

      <div className="city-detail-stats">
        <div className="card card-pad">
          <div className="muted">{t('detail.stats.rewards24h')}</div>
          <div className="num-lg">{fmt(city.total_rewards_24h_hnt)}</div>
        </div>
        <div className="card card-pad">
          <div className="muted">{t('detail.stats.rewards7d')}</div>
          <div className="num-lg">{fmt(city.total_rewards_7d_hnt)}</div>
        </div>
        <div className="card card-pad">
          <div className="muted">{t('detail.stats.rewards30d')}</div>
          <div className="num-lg">{fmt(city.total_rewards_30d_hnt)}</div>
        </div>
        <div className="card card-pad">
          <div className="muted">{t('detail.stats.online')}</div>
          <div className="num-lg">
            {city.active_hotspots_count} / {city.hotspots_count} ({city.online_pct.toFixed(1)}%)
          </div>
        </div>
      </div>

      {city.hotspots.length === 0 && (
        <div className="card card-pad state-empty" data-testid="city-detail-empty">
          <p>{t('detail.empty')}</p>
        </div>
      )}

      {city.hotspots.length > 0 && (
        <>
          <div className="list" role="list" aria-label={t('detail.hotspotsListLabel')} data-testid="city-hotspots-list">
            {city.hotspots.map((h, i) => (
              <Link
                key={h.id}
                to="/hotspots/$id"
                params={{ id: String(h.id) }}
                className="list-row"
                data-testid={`city-hotspot-row-${i}`}
              >
                <div className="cp-cell-identity">
                  <div className="identity-title">{h.name ?? t('detail.unnamedHotspot')}</div>
                  <div className="identity-sub">{h.online ? t('detail.online') : t('detail.offline')}</div>
                </div>
                <div className="cp-cell-earned num-md">{fmt(h.rewards_24h)}</div>
              </Link>
            ))}
          </div>

          {meta && meta.pages > 1 && (
            <div className="pagination" data-testid="city-hotspots-pagination">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('detail.prevPage')}
              </button>
              <span className="muted">{t('detail.pageOf', { page: meta.page, pages: meta.pages })}</span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page >= meta.pages}
                onClick={() => setPage((p) => Math.min(meta.pages, p + 1))}
              >
                {t('detail.nextPage')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
